import { formatJobNumber, invoiceListSchema, type InvoiceListParams } from '@fieldops/shared'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Icon } from '../components/Icon.js'
import { SortMenu } from '../components/ListControls.js'
import {
  Badge,
  ButtonGroup,
  Card,
  EmptyState,
  ErrorState,
  FilterChip,
  INVOICE_STATUS_TONE,
  ListHead,
  Page,
  PageHeader,
  Pagination,
  SearchBox,
  Segmented,
  SkeletonRows,
  StatStrip,
  StatTile,
  Toolbar,
  humanise,
  listRowClass,
} from '../components/ui.js'
import { cn } from '../lib/cn.js'
import { formatShortDate, money, relativeTime } from '../lib/format.js'
import { useJobHref } from '../lib/job-overlay.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../lib/trpc.js'
import { useUrlState } from '../lib/url-state.js'

/**
 * Invoices, as money in and money owed.
 *
 * The page leads with the four numbers the office is asked about — what is
 * owed, what is late, what is still a draft, what came in this month — and
 * each one is also the filter for the rows under it. The status picker is one
 * choice of views rather than seven chips, because "issued or part paid or
 * overdue" is one question ("who owes us"), not three.
 *
 * Overdue is derived from the due date and the balance, never stored; the
 * list, the tile and the badge all apply the same rule.
 */

type InvoiceRow = RouterOutputs['invoices']['list']['items'][number]
type Status = InvoiceListParams['status'][number]

type View = 'ALL' | 'DRAFT' | 'OUTSTANDING' | 'OVERDUE' | 'PAID' | 'VOID'

const VIEWS: Record<View, { label: string; status: Status[]; overdueOnly: boolean }> = {
  ALL: { label: 'All', status: [], overdueOnly: false },
  DRAFT: { label: 'Draft', status: ['DRAFT'], overdueOnly: false },
  OUTSTANDING: {
    label: 'Owing',
    status: ['ISSUED', 'PARTIALLY_PAID', 'OVERDUE'],
    overdueOnly: false,
  },
  OVERDUE: { label: 'Overdue', status: [], overdueOnly: true },
  PAID: { label: 'Paid', status: ['PAID'], overdueOnly: false },
  VOID: { label: 'Void', status: ['VOID', 'WRITTEN_OFF'], overdueOnly: false },
}

function viewOf(params: InvoiceListParams): View | null {
  if (params.overdueOnly) return 'OVERDUE'
  const key = [...params.status].sort().join(',')
  for (const [view, entry] of Object.entries(VIEWS) as Array<[View, (typeof VIEWS)[View]]>) {
    if (!entry.overdueOnly && [...entry.status].sort().join(',') === key) return view
  }
  return null
}

const SORT_FIELDS = [
  { key: 'issuedAt', label: 'Issued' },
  { key: 'dueAt', label: 'Due date' },
  { key: 'number', label: 'Number' },
  { key: 'totalIncTax', label: 'Total' },
] as const

const GRID =
  'sm:grid-cols-[7rem_minmax(0,1.6fr)_minmax(0,0.7fr)_5.5rem_9rem_6.5rem_6.5rem_6.5rem]'

export function Invoices() {
  const trpc = useTRPC()
  const [params, setParams] = useUrlState(invoiceListSchema)

  const { data, isPending, error, refetch } = useQuery(trpc.invoices.list.queryOptions(params))
  const summary = useQuery(trpc.invoices.summary.queryOptions())

  const view = viewOf(params)
  const setView = (next: View) =>
    setParams({ status: VIEWS[next].status, overdueOnly: VIEWS[next].overdueOnly, page: 1 })

  const clientName = params.clientId ? data?.items[0]?.client.name : undefined
  const filtered = Boolean(params.q) || Boolean(params.clientId) || view !== 'ALL'

  return (
    <Page>
      <PageHeader
        title="Invoices"
        description={
          summary.data
            ? `${money(summary.data.outstanding.value)} owing across ${summary.data.outstanding.count} ${
                summary.data.outstanding.count === 1 ? 'invoice' : 'invoices'
              }`
            : 'Money owed and money in'
        }
      />

      <StatStrip>
        <StatTile
          label="Owing"
          value={summary.data ? money(summary.data.outstanding.value) : '·'}
          caption={summary.data ? `${summary.data.outstanding.count} issued, not settled` : undefined}
          onClick={() => setView('OUTSTANDING')}
          active={view === 'OUTSTANDING'}
        />
        <StatTile
          label="Overdue"
          value={summary.data ? money(summary.data.overdue.value) : '·'}
          tone={summary.data && summary.data.overdue.value > 0 ? 'red' : undefined}
          caption={summary.data ? `${summary.data.overdue.count} past their due date` : undefined}
          onClick={() => setView('OVERDUE')}
          active={view === 'OVERDUE'}
        />
        <StatTile
          label="Drafts"
          value={summary.data ? summary.data.drafts.count : '·'}
          caption={summary.data ? `${money(summary.data.drafts.value)} not yet sent` : undefined}
          onClick={() => setView('DRAFT')}
          active={view === 'DRAFT'}
        />
        <StatTile
          label="Received"
          value={summary.data ? money(summary.data.receivedLast30.value) : '·'}
          tone={summary.data && summary.data.receivedLast30.value > 0 ? 'green' : undefined}
          caption="Payments in the last 30 days"
        />
      </StatStrip>

      <Card>
        <Toolbar className="border-b-0">
          <SearchBox
            value={params.q ?? ''}
            onChange={(q) => setParams({ q: q || undefined, page: 1 }, { replace: true })}
            placeholder="Number, PO or client"
            className="max-sm:w-auto max-sm:min-w-0 max-sm:flex-1"
          />
          <Segmented<View>
            className="max-sm:order-2"
            label="Which invoices"
            value={view ?? 'ALL'}
            onChange={setView}
            options={(Object.keys(VIEWS) as View[]).map((key) => ({
              value: key,
              label: VIEWS[key].label,
            }))}
          />
          {params.clientId ? (
            <FilterChip
              active
              className="max-sm:order-3"
              onClick={() => setParams({ clientId: undefined, page: 1 })}
            >
              <span className="flex items-center gap-1">
                {clientName ?? 'One client'}
                <Icon name="close" className="size-3" />
              </span>
            </FilterChip>
          ) : null}
          <ButtonGroup label="How the list reads" className="ml-auto rounded-lg max-sm:order-1">
            <SortMenu
              fields={SORT_FIELDS}
              sort={params.sort}
              dir={params.dir}
              onChange={(next) => setParams(next)}
            />
          </ButtonGroup>
        </Toolbar>
      </Card>

      <Card>
        {error ? (
          <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
        ) : isPending ? (
          <SkeletonRows />
        ) : data.items.length === 0 ? (
          <EmptyState
            icon="invoices"
            title={filtered ? 'No invoices match' : 'No invoices yet'}
            description={
              filtered
                ? 'Try another view or a shorter search.'
                : 'Invoices are raised from a job, on its Invoice tab.'
            }
          />
        ) : (
          <>
            <ListHead className={GRID}>
              <span>Number</span>
              <span>Client</span>
              <span>Job</span>
              <span>Issued</span>
              <span>Due</span>
              <span className="text-right">Total</span>
              <span className="text-right">Owing</span>
              <span className="text-right">Status</span>
            </ListHead>
            <ul className="divide-y divide-ink-100">
              {data.items.map((invoice) => (
                <li key={invoice.id}>
                  <InvoiceListRow invoice={invoice} />
                </li>
              ))}
            </ul>
          </>
        )}

        {data ? (
          <Pagination
            page={data.page}
            pageCount={data.pageCount}
            total={data.total}
            onChange={(page) => setParams({ page })}
          />
        ) : null}
      </Card>
    </Page>
  )
}

function InvoiceListRow({ invoice }: { invoice: InvoiceRow }) {
  const jobHref = useJobHref()
  const owing = invoice.totalIncTax - invoice.amountPaid
  const due = invoice.dueAt ? new Date(invoice.dueAt) : null
  const overdue =
    due !== null && due < new Date() && owing > 0 && !['VOID', 'WRITTEN_OFF', 'DRAFT'].includes(invoice.status)

  const cells = (
    <>
      <span className="text-xs font-semibold tabular-nums text-ink-700">{invoice.number}</span>

      <span
        className="order-first min-w-0 basis-full text-sm font-medium break-words text-ink-900 sm:order-none sm:basis-auto sm:truncate"
        title={invoice.client.name}
      >
        {invoice.client.name}
      </span>

      <span className="min-w-0 truncate text-[11px] tabular-nums text-ink-500">
        {invoice.job ? `Job ${formatJobNumber(invoice.job)}` : null}
        {invoice.purchaseOrderNumber ? ` · PO ${invoice.purchaseOrderNumber}` : ''}
      </span>

      <span className="hidden text-xs text-ink-600 sm:block">{formatShortDate(invoice.issuedAt)}</span>

      <span
        className={cn('truncate text-xs', overdue ? 'font-medium text-red-700' : 'text-ink-600')}
      >
        {due ? (
          <>
            <span className="sm:hidden">Due </span>
            {formatShortDate(due)}
            {overdue ? <span className="text-[11px]"> · {relativeTime(due)}</span> : null}
          </>
        ) : (
          <span className="text-ink-400">—</span>
        )}
      </span>

      <span className="hidden text-sm tabular-nums text-ink-900 sm:block sm:text-right">
        {money(invoice.totalIncTax)}
      </span>

      <span
        className={cn(
          'ml-auto text-sm tabular-nums sm:ml-0 sm:text-right',
          owing > 0 ? 'font-semibold text-ink-900' : 'text-ink-400',
        )}
      >
        {owing > 0 ? money(owing) : 'Settled'}
      </span>

      <span className="sm:text-right">
        <Badge tone={overdue ? 'red' : (INVOICE_STATUS_TONE[invoice.status] ?? 'neutral')}>
          {overdue ? 'Overdue' : humanise(invoice.status)}
        </Badge>
      </span>
    </>
  )

  // The invoice is edited on its job's Invoice tab, so the row opens the job.
  // One raised against a client with no job opens the client instead.
  return invoice.job ? (
    <Link to={jobHref(invoice.job.id)} className={cn(listRowClass, GRID)}>
      {cells}
    </Link>
  ) : (
    <Link to={`/clients/${invoice.client.id}`} className={cn(listRowClass, GRID)}>
      {cells}
    </Link>
  )
}
